import React, { useState, useMemo } from 'react'
import { EmissionsStackedAreaEChart } from './echarts/echartsCharts'
// Migration preview: this shared component now renders the ECharts stacked-area
// chart (themed to Tellus) while keeping its side table + layout. The old
// Highcharts option builder is removed; the `colors` prop is ignored so the
// airy Tellus-blue theme palette applies.

interface DataItem {
  [key: string]: any
}

interface EmissionsStackedChartProps {
  data?: DataItem[]
  xKey?: string
  minHeight?: number
  title?: string
  yAxisLabel?: string
  showTable?: boolean
  colors?: string[]
}

const EmissionsStackedChart = ({
  data = [],
  xKey = 'year',
  minHeight = 500,
  yAxisLabel = "Total emitted (tCO₂e)",
  colors,
  showTable = true,
}: EmissionsStackedChartProps) => {
  const [sortField, setSortField] = useState(xKey)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10


  if (!data || data.length === 0) {
    return (
      <div style={{ height: minHeight }} className="flex items-center justify-center rounded-lg border border-gray-200 bg-white shadow-xs">
        <div className="text-center">
          <div className="text-gray-500 text-lg">No data available</div>
        </div>
      </div>
    )
  }

  // Extract series keys (excluding the x-axis key)
  const seriesKeys = Object.keys(data[0]).filter(key => key !== xKey)

  // Drop empty years (all series zero) so a trailing/no-data year like 2031
  // does not show as a blank bar and an all-zero table row.
  const rows = data.filter(item => seriesKeys.some(k => (parseFloat(String(item[k])) || 0) !== 0))

  // Prepare table data with sorting and pagination
  const sortedData = useMemo(() => {
    const sorted = [...rows].sort((a, b) => {
      const aVal = a[sortField]
      const bVal = b[sortField]

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
      }

      const aStr = String(aVal)
      const bStr = String(bVal)
      return sortDirection === 'asc'
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr)
    })
    return sorted
  }, [data, sortField, sortDirection])

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    return sortedData.slice(startIndex, startIndex + itemsPerPage)
  }, [sortedData, currentPage])

  const totalPages = Math.ceil(sortedData.length / itemsPerPage)

  // Calculate totals for each series
  const totals = seriesKeys.reduce((acc: Record<string, number>, key) => {
    acc[key] = rows.reduce((sum, item) => sum + (parseFloat(item[key]) || 0), 0)
    return acc
  }, {})

  const formatNumber = (value: any) => {
    if (value === null || value === undefined || isNaN(value)) return '0'
    return parseFloat(value).toLocaleString(undefined, {
      maximumFractionDigits: 2
    })
  }

  const chart = (
    <EmissionsStackedAreaEChart data={rows} xKey={xKey} yAxisLabel={yAxisLabel} colors={colors} height={minHeight} />
  )

  if (!showTable) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white shadow-xs p-4">
        {chart}
      </div>
    )
  }

  return (
    <div style={{ height: minHeight }} className="flex rounded-lg border border-gray-200 bg-white shadow-xs">
      {/* Chart Section */}
      <div style={{ width: '60%' }} className="flex items-center py-6 pl-8 pr-2">
        {chart}
      </div>

      {/* Table Section */}
      <div style={{ width: '40%', height: minHeight }} className="pl-4 pr-6 pt-10 flex flex-col">
        <div className="mb-4">
          <div className="text-sm font-semibold text-gray-700">Emission Breakdown</div>
        </div>
        <div className="flex-1 overflow-auto pt-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="text-left py-2 pl-2 text-[11px] font-semibold uppercase tracking-wide text-gray-700">
                  {xKey === 'year' ? 'Year' : xKey}
                </th>
                {seriesKeys.map((key, index) => (
                  <th key={key} className="text-right py-2 pr-2 text-[11px] font-semibold uppercase tracking-wide text-gray-700">
                    {key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => (
                <tr key={index} className="even:bg-gray-50 hover:bg-gray-100">
                  <td className="py-1 pl-2 font-medium text-gray-800">{item[xKey]}</td>
                  {seriesKeys.map(key => (
                    <td key={key} className="py-1 pr-2 text-right tabular-nums text-gray-700">
                      {formatNumber(item[key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {data.length > 0 && (
              <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                <tr>
                  <td className="py-1 pl-2 font-bold text-gray-900">Total</td>
                  {seriesKeys.map(key => (
                    <td key={key} className="py-1 pr-2 text-right tabular-nums font-bold text-gray-900">
                      {formatNumber(totals[key])}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <div className="text-sm text-gray-500">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedData.length)} of {sortedData.length} entries
            </div>
            <div className="flex space-x-1">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-xs border rounded-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = i + 1
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 text-xs border rounded-sm ${
                      currentPage === page
                        ? 'bg-perfect-shine-500 text-white border-perfect-shine-500'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    {page}
                  </button>
                )
              })}
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-xs border rounded-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default EmissionsStackedChart
